import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

// Back-office (super admin) list of settled card charges with no Supernova
// order. Never used by tenant pages.

export type PaymentReconStatus = 'open' | 'resolved' | 'auto_resolved' | 'all';

export interface PaymentReconAlert {
  id: string;
  tenantId: string;
  tenantName: string | null;
  locationId: string | null;
  locationName: string | null;
  fundingDate: string;
  amount: string;
  authCode: string | null;
  lastFour: string | null;
  brand: string | null;
  transactionNumber: string | null;
  status: Exclude<PaymentReconStatus, 'all'>;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  resolutionNote: string | null;
}

export interface PaymentReconRunSummary {
  stores: number;
  outOfScopeDeleted: number;
  expiredDeleted: number;
  unmatched: number;
  newlyOpened: number;
  autoResolved: number;
  openTotal: number;
}

const ALERTS_KEY = 'admin-payment-recon-alerts';

export interface PaymentReconFilters {
  status: PaymentReconStatus;
  page: number;
  perPage: number;
  tenantId?: string;
  /** Settled date range, YYYY-MM-DD inclusive. */
  from?: string;
  to?: string;
  /** Amount, card last 4 or approval code. */
  q?: string;
}

export function usePaymentReconAlerts(params: PaymentReconFilters) {
  const query: Record<string, string | number | undefined> = { ...params };
  return useQuery({
    queryKey: [ALERTS_KEY, params],
    queryFn: () =>
      api.get<{
        data: PaymentReconAlert[];
        totals: { count: number; amount: string };
        meta: { page: number; perPage: number; total: number; totalPages: number };
      }>('/admin/payment-recon/alerts', query),
  });
}

export function useResolvePaymentReconAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; note: string }) =>
      api.post(`/admin/payment-recon/alerts/${args.id}/resolve`, { note: args.note }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ALERTS_KEY] });
    },
  });
}

export function useRunPaymentReconCheck() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ data: PaymentReconRunSummary }>('/admin/payment-recon/run'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ALERTS_KEY] });
    },
  });
}
